(function () {
  'use strict';

  class DeveloplyCart {
    constructor() {
      this.drawer    = document.getElementById('developly-cart');
      if (!this.drawer) return;

      this.overlay       = document.getElementById('developly-cart-overlay');
      this.body          = document.getElementById('dev-cart-body');
      this.emptyEl       = document.getElementById('dev-cart-empty');
      this.filledEl      = document.getElementById('dev-cart-filled');
      this.itemsEl       = document.getElementById('dev-cart-items');
      this.loaderEl      = document.getElementById('dev-cart-loader');
      this.shippingMsg   = document.getElementById('dev-cart-shipping-msg');
      this.progressFill  = document.getElementById('dev-cart-progress-fill');
      this.progressIcon  = document.getElementById('dev-cart-progress-icon');
      this.checkoutPrice = document.getElementById('dev-checkout-pricing');
      this.promoInput    = document.getElementById('dev-promo-input');
      this.promoMsg      = document.getElementById('dev-promo-msg');
      this.insuranceChk  = document.getElementById('dev-insurance-chk');

      this.cart               = null;
      this.isOpen             = false;
      this.isUpdating         = false;
      this.lastAppliedCode    = null; /* shown in fallback when API doesn't return code list */
      this.freeShippingCents  = parseInt(this.drawer.dataset.freeShipping || '6900', 10);
      this.insuranceVariantId = this.drawer.dataset.insuranceVariant || null;

      /* Money format — use Shopify's shop.money_format string, passed via data attribute.
         Falls back to a generic placeholder if somehow missing. */
      this.moneyFormat = this.drawer.dataset.moneyFormat || '{{amount}}';

      /* i18n strings — all read from data-i18n-* attributes set by Liquid */
      this.i18n = {
        shippingFree:      this.drawer.dataset.i18nShippingFree      || 'Free delivery!',
        shippingRemaining: this.drawer.dataset.i18nShippingRemaining || 'Only __AMOUNT__ left for free delivery!',
        remove:            this.drawer.dataset.i18nRemove            || 'Remove',
        decrease:          this.drawer.dataset.i18nDecrease          || '-',
        increase:          this.drawer.dataset.i18nIncrease          || '+',
        quantityLabel:     this.drawer.dataset.i18nQuantityLabel     || 'Quantity for __TITLE__',
        crossAdd:          this.drawer.dataset.i18nCrossAdd          || 'Add',
        crossAdded:        this.drawer.dataset.i18nCrossAdded        || 'Added ✓',
        promoEmpty:        this.drawer.dataset.i18nPromoEmpty        || 'Please enter a promo code.',
        promoSuccess:      this.drawer.dataset.i18nPromoSuccess      || 'Promo code applied.',
        promoError:        this.drawer.dataset.i18nPromoError         || 'Invalid or expired code.',
        promoAppliedLabel:   this.drawer.dataset.i18nPromoAppliedLabel   || 'Applied code(s)',
        promoDiscountApplied: this.drawer.dataset.i18nPromoDiscountApplied || 'Discount applied',
        promoRemove:         this.drawer.dataset.i18nPromoRemove          || 'Remove',
      };

      this._init();
    }

    /* ══════════════════════════════════════════
       INIT
    ══════════════════════════════════════════ */
    async _init() {
      this._bindEvents();
      await this._fetchCart();
      this._render();
    }

    /* ══════════════════════════════════════════
       EVENTS
    ══════════════════════════════════════════ */
    _bindEvents() {
      /* Open triggers — add data-dev-cart-open to any element */
      document.addEventListener('click', (e) => {
        if (e.target.closest('[data-dev-cart-open]')) {
          e.preventDefault();
          this.open();
        }
      });

      /* Custom events for external scripts */
      document.addEventListener('developly:cart:open', () => this.open());
      document.addEventListener('developly:cart:refresh', async () => {
        await this._fetchCart();
        this._render();
      });

      /* Udesly (Webflow) AJAX add-to-cart dispatches on its event bus — there is no
         commerce-cart-wrapper in the header anymore, so nothing else listened.
         Bridge so collection cards + product forms refresh this drawer and open it. */
      this._bindUdeslyBridge();

      /* Close */
      document.getElementById('dev-cart-close')?.addEventListener('click', () => this.close());
      this.overlay?.addEventListener('click', () => this.close());
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen) this.close();
      });

      /* Quantity + remove — event delegation */
      this.itemsEl?.addEventListener('click', (e) => {
        const dec = e.target.closest('[data-dev-dec]');
        const inc = e.target.closest('[data-dev-inc]');
        const del = e.target.closest('[data-dev-del]');
        if (dec && !this.isUpdating) this._updateQty(dec.dataset.devDec, -1);
        if (inc && !this.isUpdating) this._updateQty(inc.dataset.devInc, +1);
        if (del && !this.isUpdating) this._removeItem(del.dataset.devDel);
      });

      /* Upsell form */
      document.getElementById('dev-cart-upsell-form')?.addEventListener('submit', async (e) => {
        e.preventDefault();
        if (this.isUpdating) return;
        const form      = e.currentTarget;
        const select    = form.querySelector('select[name="id"]');
        const hidden    = form.querySelector('input[name="id"]');
        const variantId = parseInt((select ? select.value : hidden?.value) || '0', 10);
        if (!variantId) return;
        const btn = form.querySelector('.dev-cart__upsell-btn');
        btn?.classList.add('is-loading');
        await this._addToCart(variantId, 1);
        this._render();
        btn?.classList.remove('is-loading');
      });

      /* Upsell variant change → update displayed price */
      document.getElementById('dev-upsell-variant')?.addEventListener('change', (e) => {
        const priceCents = parseInt(e.target.options[e.target.selectedIndex].dataset.price || '0', 10);
        const priceEl    = document.getElementById('dev-upsell-price');
        if (priceEl && priceCents) priceEl.textContent = this._money(priceCents);
      });

      /* Promo code */
      document.getElementById('dev-promo-btn')?.addEventListener('click', () => this._applyPromo());
      this.promoInput?.addEventListener('keydown', (e) => { if (e.key === 'Enter') this._applyPromo(); });

      /* Insurance toggle */
      this.insuranceChk?.addEventListener('change', async (e) => {
        if (!this.insuranceVariantId || this.isUpdating) {
          e.target.checked = !e.target.checked;
          return;
        }
        await this._toggleInsurance(e.target.checked);
      });

      /* Cross-sell add buttons */
      document.addEventListener('click', async (e) => {
        const btn = e.target.closest('.dev-cart__cross-add[data-variant-id]');
        if (!btn || this.isUpdating) return;
        const variantId = parseInt(btn.dataset.variantId, 10);
        if (!variantId) return;
        btn.classList.add('is-loading');
        await this._addToCart(variantId, 1);
        btn.classList.remove('is-loading');
        btn.classList.add('is-added');
        const orig = btn.textContent;
        btn.textContent = this.i18n.crossAdded;
        this._render();
        setTimeout(() => { btn.classList.remove('is-added'); btn.textContent = orig; }, 2500);
      });

      /* Cross-sell scroll navigation */
      document.getElementById('dev-cross-prev')?.addEventListener('click', () => {
        document.getElementById('dev-cross-scroll')?.scrollBy({ left: -273, behavior: 'smooth' });
      });
      document.getElementById('dev-cross-next')?.addEventListener('click', () => {
        document.getElementById('dev-cross-scroll')?.scrollBy({ left: 273, behavior: 'smooth' });
      });

      /* Applied discount codes — remove one or all */
      document.getElementById('dev-cart-applied-codes')?.addEventListener('click', async (e) => {
        const removeBtn = e.target.closest('.dev-cart__code-tag-x[data-dev-remove-code]');
        const removeAll  = e.target.closest('.dev-cart__code-tag-x[data-dev-remove-all-codes]');
        if (this.isUpdating) return;
        if (removeBtn) {
          const codeToRemove = removeBtn.dataset.devRemoveCode;
          const codes        = this._getAppliedDiscountCodes();
          const next         = codes.filter((c) => c !== codeToRemove);
          await this._updateCartDiscount(next.length ? next.join(',') : '');
          return;
        }
        if (removeAll) {
          this.lastAppliedCode = null;
          await this._updateCartDiscount('');
        }
      });
    }

    _bindUdeslyBridge() {
      const U = window.Udesly;
      if (!U || typeof U.on !== 'function') return;

      const syncFromServer = async () => {
        await this._fetchCart();
        this._render();
      };

      U.on('product-added', (shouldOpen) => {
        void (async () => {
          await syncFromServer();
          /* Default Udesly behavior: open cart when detail is true/undefined; skip when false */
          if (shouldOpen !== false) this.open();
        })();
      });

      U.on('cart-updated', () => {
        void syncFromServer();
      });

      U.on('cart-should-be-updated', () => {
        void syncFromServer();
      });
    }

    /* ══════════════════════════════════════════
       SHOPIFY AJAX CART API
    ══════════════════════════════════════════ */
    async _fetchCart() {
      try {
        const r = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        if (!r.ok) throw new Error(r.statusText);
        this.cart = await r.json();
        await this._enrichCompareAtPrices();
      } catch (err) {
        /* Cart fetch failed; leave existing state. No console in production. */
      }
    }

    async _enrichCompareAtPrices() {
      if (!this.cart || !this.cart.items || !this.cart.items.length) return;
      const handles = [...new Set(this.cart.items.map(i => i.handle))];
      const productData = {};
      await Promise.all(handles.map(async (handle) => {
        try {
          const r = await fetch(`/products/${handle}.js`, { headers: { Accept: 'application/json' } });
          if (r.ok) productData[handle] = await r.json();
        } catch (e) { /* skip */ }
      }));
      for (const item of this.cart.items) {
        const prod = productData[item.handle];
        if (!prod) continue;
        const v = prod.variants.find(v => v.id === item.variant_id || v.id === item.id);
        if (v && v.compare_at_price) {
          item._compare_at_price = Number(v.compare_at_price) * item.quantity;
        }
      }
    }

    async _changeCart(key, quantity) {
      const r = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity }),
      });
      if (!r.ok) throw new Error(await r.text());
      this.cart = await r.json();
    }

    async _addToCart(variantId, quantity, properties) {
      const r = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: variantId, quantity, properties: properties || {} }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.description || "Erreur lors de l'ajout au panier");
      }
      await this._fetchCart();
    }

    /* ══════════════════════════════════════════
       ACTIONS
    ══════════════════════════════════════════ */
    async _updateQty(key, delta) {
      const item = this.cart?.items.find((i) => i.key === key);
      if (!item) return;
      const newQty = Math.max(0, item.quantity + delta);
      this.isUpdating = true;
      try {
        await this._changeCart(key, newQty);
        this._syncInsuranceToggle();
        this._render();
      } catch (err) {
        /* Update failed; keep current state */
      } finally {
        this.isUpdating = false;
      }
    }

    async _removeItem(key) {
      this.isUpdating = true;
      try {
        await this._changeCart(key, 0);
        this._syncInsuranceToggle();
        this._render();
      } catch (err) {
        /* Remove failed; keep current state */
      } finally {
        this.isUpdating = false;
      }
    }

    async _toggleInsurance(add) {
      if (!this.insuranceVariantId) return;
      this.isUpdating = true;
      try {
        if (add) {
          await this._addToCart(parseInt(this.insuranceVariantId, 10), 1);
        } else {
          const item = this.cart?.items.find(
            (i) => String(i.variant_id) === String(this.insuranceVariantId)
          );
          if (item) await this._changeCart(item.key, 0);
        }
        this._render();
      } catch (err) {
        /* Toggle failed; sync checkbox state */
      } finally {
        this.isUpdating = false;
        this._syncInsuranceToggle();
      }
    }

    async _updateCartDiscount(discountValue) {
      this.isUpdating = true;
      try {
        const r = await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ discount: discountValue }),
        });
        if (!r.ok) throw new Error(await r.text());
        this.cart = await r.json();
        this._render();
      } catch (err) {
        /* Discount update failed; keep current state */
      } finally {
        this.isUpdating = false;
      }
    }

    _getAppliedDiscountCodes() {
      const apps = this.cart?.cart_level_discount_applications;
      if (Array.isArray(apps) && apps.length > 0) {
        return apps.map((a) => (a.title != null ? String(a.title).trim() : '')).filter(Boolean);
      }
      if (typeof this.cart?.total_discount === 'number' && this.cart.total_discount > 0) {
        return [];
      }
      return [];
    }

    _renderAppliedCodes() {
      const container = document.getElementById('dev-cart-applied-codes');
      if (!container) return;
      const codes = this._getAppliedDiscountCodes();
      const hasDiscount = codes.length > 0 ||
        (typeof this.cart?.total_discount === 'number' && this.cart.total_discount > 0);
      if (!hasDiscount) {
        this.lastAppliedCode = null;
        container.classList.remove('is-visible');
        container.innerHTML = '';
        return;
      }
      container.classList.add('is-visible');
      const remove = this._esc(this.i18n.promoRemove);
      if (codes.length > 0) {
        this.lastAppliedCode = null;
        container.innerHTML =
          '<div class="dev-cart__code-tags">' +
          codes.map((code) =>
            '<span class="dev-cart__code-tag">' +
            '<span class="dev-cart__code-tag-name">' + this._esc(code) + '</span>' +
            '<button type="button" class="dev-cart__code-tag-x" data-dev-remove-code="' + this._esc(code) + '" aria-label="' + remove + ' ' + this._esc(code) + '">&times;</button>' +
            '</span>'
          ).join('') +
          '</div>';
      } else {
        const displayCode = this.lastAppliedCode ? this._esc(this.lastAppliedCode) : null;
        const discountLabel = this._esc(this.i18n.promoDiscountApplied);
        container.innerHTML =
          '<div class="dev-cart__code-tags">' +
          '<span class="dev-cart__code-tag">' +
          (displayCode ? '<span class="dev-cart__code-tag-name">' + displayCode + '</span>' : '<span class="dev-cart__code-tag-fallback">' + discountLabel + '</span>') +
          '<button type="button" class="dev-cart__code-tag-x" data-dev-remove-all-codes aria-label="' + remove + '">&times;</button>' +
          '</span>' +
          '</div>';
      }
    }

    async _applyPromo() {
      const code = this.promoInput?.value.trim();
      if (!code) {
        this._setPromoMsg(this.i18n.promoEmpty, 'err');
        return;
      }
      const btn = document.getElementById('dev-promo-btn');
      if (btn) {
        btn.disabled = true;
        btn.setAttribute('aria-busy', 'true');
      }
      this._setPromoMsg('', '');
      const prevDiscount = (this.cart && typeof this.cart.total_discount === 'number') ? this.cart.total_discount : 0;
      const applyUrl     = '/discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent(window.location.pathname + window.location.search);
      try {
        const r = await fetch(applyUrl, { method: 'GET', credentials: 'same-origin', redirect: 'follow' });
        if (!r.ok) throw new Error('Discount request failed');
        await this._fetchCart();
        const hasNewDiscount = typeof this.cart?.total_discount === 'number' && this.cart.total_discount > prevDiscount;
        if (hasNewDiscount) {
          this.lastAppliedCode = code;
          this._setDiscountCookie(code);
          this._setPromoMsg(this.i18n.promoSuccess, 'ok');
          if (this.promoInput) this.promoInput.value = '';
          this._render();
        } else {
          this._setPromoMsg(this.i18n.promoError, 'err');
        }
      } catch (err) {
        this._setPromoMsg(this.i18n.promoError, 'err');
      } finally {
        if (btn) {
          btn.disabled = false;
          btn.removeAttribute('aria-busy');
        }
      }
    }

    /* ══════════════════════════════════════════
       OPEN / CLOSE
    ══════════════════════════════════════════ */
    open() {
      if (this.isOpen) return;
      this.isOpen = true;
      this._focusPrevious = document.activeElement;
      this.drawer.classList.add('is-open');
      this.drawer.setAttribute('aria-hidden', 'false');
      this.overlay?.classList.add('is-open');
      this.overlay?.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      this._fetchCart().then(() => this._render());
      this._bindFocusTrap();
      requestAnimationFrame(() => {
        const first = this._getFocusables()[0];
        if (first) first.focus();
      });
    }

    close() {
      if (!this.isOpen) return;
      this.isOpen = false;
      this._unbindFocusTrap();
      this.drawer.classList.remove('is-open');
      this.drawer.setAttribute('aria-hidden', 'true');
      this.overlay?.classList.remove('is-open');
      this.overlay?.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      if (this._focusPrevious && typeof this._focusPrevious.focus === 'function') {
        this._focusPrevious.focus();
      }
      this._focusPrevious = null;
    }

    _getFocusables() {
      const sel = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
      return Array.prototype.slice.call(this.drawer.querySelectorAll(sel));
    }

    _bindFocusTrap() {
      this._focusTrapHandler = (e) => {
        if (e.key !== 'Tab') return;
        const focusables = this._getFocusables();
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      };
      this.drawer.addEventListener('keydown', this._focusTrapHandler);
    }

    _unbindFocusTrap() {
      if (this._focusTrapHandler) {
        this.drawer.removeEventListener('keydown', this._focusTrapHandler);
        this._focusTrapHandler = null;
      }
    }

    /* ══════════════════════════════════════════
       RENDER
    ══════════════════════════════════════════ */
    _render() {
      if (!this.cart) return;
      const displayItems = this._getDisplayItems();
      const isEmpty = displayItems.length === 0;

      if (this.emptyEl) {
        this.emptyEl.style.display = isEmpty ? 'flex' : 'none';
        this.emptyEl.classList.toggle('is-visible', isEmpty);
      }
      if (this.filledEl) {
        this.filledEl.style.display = isEmpty ? 'none' : 'flex';
        this.filledEl.classList.toggle('is-visible', !isEmpty);
      }

      if (!isEmpty) {
        this._renderShipping(displayItems);
        this._renderItems(displayItems);
        this._renderTotals(displayItems);
        this._syncInsuranceToggle();
        this._renderAppliedCodes();
      } else {
        this._renderAppliedCodes();
      }

      /* Update cart count badges sitewide */
      const totalQty = this._getDisplayItems().reduce((s, i) => s + i.quantity, 0);
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = totalQty;
        el.style.display = totalQty > 0 ? 'inline-block' : 'none';
      });

      /* Checkout link: pass discount in URL so checkout receives the code */
      const checkoutLink = document.getElementById('dev-checkout-cta');
      if (checkoutLink) {
        const codes = this._getAppliedDiscountCodes();
        const code = codes.length > 0 ? codes[0] : (this.lastAppliedCode || '');
        if (code) {
          this._setDiscountCookie(code);
          checkoutLink.href = '/checkout?discount=' + encodeURIComponent(code);
        } else {
          this._clearDiscountCookie();
          checkoutLink.href = '/checkout';
        }
      }
    }

    _getDisplayItems() {
      if (!this.cart?.items) return [];
      if (!this.insuranceVariantId) return this.cart.items;
      return this.cart.items.filter(
        (i) => String(i.variant_id) !== String(this.insuranceVariantId)
      );
    }

    _renderShipping(items) {
      if (!this.shippingMsg || !this.progressFill) return;
      const subtotal  = items.reduce((s, i) => s + i.line_price, 0);
      const remaining = Math.max(0, this.freeShippingCents - subtotal);
      const pct       = Math.min(100, (subtotal / this.freeShippingCents) * 100);

      this.progressFill.style.width = pct + '%';

      if (remaining <= 0) {
        this.shippingMsg.innerHTML = '<em>' + this._esc(this.i18n.shippingFree) + '</em> 🎉';
        if (this.progressIcon) this.progressIcon.style.opacity = '0';
      } else {
        const amountStr = this._money(remaining);
        const msg       = this.i18n.shippingRemaining.replace('__AMOUNT__', '<strong>' + amountStr + '</strong>');
        this.shippingMsg.innerHTML = '<em>' + msg + '</em>';
        if (this.progressIcon) this.progressIcon.style.opacity = '1';
      }
    }

    _renderItems(items) {
      if (!this.itemsEl) return;
      if (this.loaderEl) this.loaderEl.style.display = 'none';
      this.itemsEl.innerHTML = items.map((item) => this._itemHTML(item)).join('');
    }

    _itemHTML(item) {
      const imgSrc = item.featured_image?.url
        ? this._imgSrc(item.featured_image.url, '176x176')
        : item.image
          ? this._imgSrc(item.image, '176x176')
          : '';

      const variantLabel =
        item.variant_title && item.variant_title !== 'Default Title'
          ? '<p class="dev-cart__item-variant">' + this._esc(item.variant_title) + '</p>'
          : '';

      const canDecrease   = item.quantity > 1;
      const qtyLabel      = this.i18n.quantityLabel.replace('__TITLE__', this._esc(item.product_title));
      const decreaseLabel = this.i18n.decrease;
      const increaseLabel = this.i18n.increase;
      const removeLabel   = this.i18n.remove;

      return /* html */ `
        <div class="dev-cart__item" data-key="${item.key}">
          <div class="dev-cart__item-img">
            ${imgSrc ? `<img src="${imgSrc}" alt="${this._esc(item.product_title)}" loading="lazy" width="88" height="88">` : ''}
          </div>
          <div class="dev-cart__item-mid">
            <p class="dev-cart__item-title">${this._esc(item.product_title)}</p>
            ${variantLabel}
            <div class="dev-cart__item-qty" role="group" aria-label="${qtyLabel}">
              <button type="button" class="dev-cart__qty-btn" data-dev-dec="${item.key}" aria-label="${decreaseLabel}" ${canDecrease ? '' : 'disabled'}>
                <svg width="12" height="2" viewBox="0 0 12 2" fill="none" aria-hidden="true">
                  <rect x="0" y="0.25" width="12" height="1.5" rx="0.75" fill="white"/>
                </svg>
              </button>
              <span class="dev-cart__qty-val" aria-live="polite">${item.quantity}</span>
              <button type="button" class="dev-cart__qty-btn" data-dev-inc="${item.key}" aria-label="${increaseLabel}">
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                  <rect x="0" y="5.25" width="12" height="1.5" rx="0.75" fill="white"/>
                  <rect x="5.25" y="0" width="1.5" height="12" rx="0.75" fill="white"/>
                </svg>
              </button>
            </div>
          </div>
          <div class="dev-cart__item-right">
            ${item._compare_at_price && item._compare_at_price > item.line_price ? `<p class="dev-cart__item-compare-price">${this._money(item._compare_at_price)}</p>` : ''}
            <p class="dev-cart__item-price">${this._money(item.line_price)}</p>
            <button type="button" class="dev-cart__item-delete" data-dev-del="${item.key}" aria-label="${removeLabel} ${this._esc(item.product_title)}">
              ${removeLabel}
            </button>
          </div>
        </div>`;
    }

    _renderTotals(items) {
      const totalPrice = typeof this.cart.total_price === 'number' ? this.cart.total_price : 0;
      const compareTotal = items.reduce((sum, i) => sum + (i._compare_at_price || i.original_line_price || i.line_price), 0);

      if (this.checkoutPrice) {
        if (compareTotal > totalPrice) {
          this.checkoutPrice.innerHTML =
            '<span class="dev-cart__compare">' + this._money(compareTotal) + '</span>' +
            '<span class="dev-cart__total">'   + this._money(totalPrice)   + '</span>';
        } else {
          this.checkoutPrice.innerHTML =
            '<span class="dev-cart__total">' + this._money(totalPrice) + '</span>';
        }
      }
    }

    _syncInsuranceToggle() {
      if (!this.insuranceChk || !this.insuranceVariantId) return;
      const has = !!this.cart?.items.find(
        (i) => String(i.variant_id) === String(this.insuranceVariantId)
      );
      this.insuranceChk.checked = has;
    }

    /* ══════════════════════════════════════════
       HELPERS
    ══════════════════════════════════════════ */

    /**
     * Format cents using Shopify's money_format string.
     * Supports: {{amount}}, {{amount_no_decimals}}, {{amount_with_comma_separator}},
     *           {{amount_no_decimals_with_comma_separator}}, {{amount_with_apostrophe_separator}}
     */
    _money(cents) {
      const amount = cents / 100;

      return this.moneyFormat
        .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, () =>
          Math.round(amount).toLocaleString('de-CH').replace(/\./g, "'")
        )
        .replace(/\{\{\s*amount_with_apostrophe_separator\s*\}\}/, () =>
          amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, "'")
        )
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/, () =>
          Math.round(amount).toLocaleString('en-US').replace(/,/g, ',')
        )
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, () =>
          amount.toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.')
        )
        .replace(/\{\{\s*amount\s*\}\}/, () =>
          amount.toFixed(2)
        );
    }

    _imgSrc(src, size) {
      if (!src) return '';
      return src
        .replace(/^http:/, 'https:')
        .replace(/(_\d+x\d+)?(\.(?:gif|jpg|jpeg|png|webp))(\?.*)?$/i, '_' + size + '$2$3');
    }

    _esc(str) {
      return String(str ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    }

    _setPromoMsg(msg, type) {
      if (!this.promoMsg) return;
      this.promoMsg.textContent = msg;
      this.promoMsg.className   = 'dev-cart__promo-msg' + (type ? ' ' + type : '');
    }

    _setDiscountCookie(code) {
      if (!code) return;
      const maxAge = 30 * 24 * 60 * 60;
      document.cookie = 'discount_code=' + encodeURIComponent(code) + '; path=/; max-age=' + maxAge + '; SameSite=Lax';
    }

    _clearDiscountCookie() {
      document.cookie = 'discount_code=; path=/; max-age=0';
    }
  }

  /* ── Boot ── */
  function boot() {
    if (!window.developlyCart) {
      window.developlyCart = new DeveloplyCart();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
